import { FormEvent, KeyboardEvent, useMemo, useState } from 'react';
import { SupportToneBadge } from './SupportToneBadge';

const DEFAULT_SUGGESTIONS = [
  'vip',
  'urgente',
  'acesso',
  'billing',
  'bug',
  'integração',
  'retrabalho',
  'dúvida',
];

type SupportTagsPickerProps = {
  value?: string | null;
  disabled?: boolean;
  saving?: boolean;
  suggestions?: string[];
  onCommit: (tagsCsv: string) => void | Promise<void>;
};

function parseTags(value?: string | null): string[] {
  if (!value?.trim()) return [];
  return value
    .split(',')
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);
}

export function SupportTagsPicker({
  value,
  disabled = false,
  saving = false,
  suggestions = DEFAULT_SUGGESTIONS,
  onCommit,
}: SupportTagsPickerProps) {
  const tags = useMemo(() => parseTags(value), [value]);
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);

  const availableSuggestions = suggestions.filter(
    (item) => !tags.includes(item.toLowerCase()),
  );

  async function commitTags(next: string[]): Promise<void> {
    const normalized = Array.from(new Set(next.map((item) => item.trim().toLowerCase()).filter(Boolean)));
    await onCommit(normalized.join(', '));
  }

  async function addTag(raw: string): Promise<void> {
    const tag = raw.trim().toLowerCase();
    if (!tag || tags.includes(tag)) {
      setDraft('');
      return;
    }
    setDraft('');
    await commitTags([...tags, tag]);
  }

  async function removeTag(tag: string): Promise<void> {
    await commitTags(tags.filter((item) => item !== tag));
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      void addTag(draft);
    }
    if (event.key === 'Backspace' && !draft && tags.length > 0) {
      void removeTag(tags[tags.length - 1]);
    }
  }

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    void addTag(draft);
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {tags.length === 0 ? (
          <span className="text-sm text-secondary-500">Nenhuma tag</span>
        ) : (
          tags.map((tag) => (
            <SupportToneBadge key={tag} tone="neutral">
              <button
                type="button"
                className="inline-flex items-center gap-1 disabled:opacity-60"
                disabled={disabled || saving}
                onClick={() => void removeTag(tag)}
                title="Remover tag"
              >
                {tag}
                <span aria-hidden className="text-secondary-400">
                  ×
                </span>
              </button>
            </SupportToneBadge>
          ))
        )}
      </div>

      {!disabled ? (
        <form onSubmit={onSubmit} className="space-y-2">
          <input
            className="erp-input w-full text-sm"
            value={draft}
            disabled={saving}
            placeholder="Adicionar tag e Enter"
            onFocus={() => setOpen(true)}
            onBlur={() => window.setTimeout(() => setOpen(false), 150)}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={onKeyDown}
          />
          {open && availableSuggestions.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {availableSuggestions.slice(0, 8).map((item) => (
                <button
                  key={item}
                  type="button"
                  className="rounded-md bg-secondary-50 px-2 py-0.5 text-[11px] text-secondary-700 ring-1 ring-inset ring-secondary-200 hover:bg-secondary-100"
                  disabled={saving}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => void addTag(item)}
                >
                  + {item}
                </button>
              ))}
            </div>
          ) : null}
        </form>
      ) : null}
    </div>
  );
}
